import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import TextOverflowTooltip from './UI/EllipsisWithTooltip';
import UI from './UI';

const ChildCollapsibleRow = styled(props => <TableRow {...props} />)`
  .MuiTableRow-root {
    background-color: #f5f5f5;
  }
`;

const TableCollapsibleRow = ({
  columns,
  rowIndex,
  editableColumnChildren,
  activeRow,
  childRows,
  ...tableRowProps
}) => {
  const activeMode = rowIndex === activeRow;
  const [open, setOpen] = useState(false);
  const {
    table: { headers, customizedWidths },
    iconColumnIndex = 1,
  } = tableRowProps;

  return (
    <>
      <TableRow {...tableRowProps}>
        {columns.map((column, cellIndex) => {
          const {
            props: {
              editableColumn = false,
              hoverStateIcons = false,
              activeStateIcons = false,
            } = {},
          } = column || {};

          if (editableColumn) {
            const { props: { value = '' } = {} } = column || {};

            return (
              <TableCell
                key={`${rowIndex}-${cellIndex}`}
                style={{
                  width: customizedWidths && customizedWidths[cellIndex],
                }}
              >
                {activeMode ? editableColumnChildren : value}
              </TableCell>
            );
          }

          if (hoverStateIcons || activeStateIcons) {
            if (
              (!activeMode && activeStateIcons) ||
              (activeMode && hoverStateIcons)
            )
              return null;

            return (
              <TableCell key={`${rowIndex}-${cellIndex}`} align="right">
                <UI components={column} context={tableRowProps} />
              </TableCell>
            );
          }

          if (childRows && childRows.length && cellIndex === iconColumnIndex) {
            return (
              <TableCell
                key={`${rowIndex}-${cellIndex}`}
                style={{
                  width: customizedWidths && customizedWidths[cellIndex],
                  paddingLeft: '5px',
                }}
              >
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </IconButton>
                {typeof column === 'object' ? (
                  <UI components={column} context={tableRowProps} />
                ) : (
                  column
                )}
              </TableCell>
            );
          }

          return (
            <TableCell
              key={`${rowIndex}-${cellIndex}`}
              style={{
                width: customizedWidths && customizedWidths[cellIndex],
              }}
            >
              {typeof column === 'object' ? (
                <UI components={column} context={tableRowProps} />
              ) : (
                <TextOverflowTooltip
                  tooltip={column}
                  text={column}
                  placement="top"
                />
              )}
            </TableCell>
          );
        })}
      </TableRow>
      {childRows && (
        <ChildCollapsibleRow {...tableRowProps}>
          <TableCell
            style={{
              padding: 0,
            }}
            colSpan={customizedWidths && customizedWidths.length}
          >
            <Collapse in={open} unmountOnExit>
              <Box>
                <Table
                  size="small"
                  style={{
                    tableLayout: 'fixed',
                  }}
                >
                  <colgroup>
                    {columns.map((column, cellIndex) => (
                      <col
                        key={cellIndex}
                        style={{
                          width:
                            customizedWidths && customizedWidths[cellIndex],
                        }}
                        span={1}
                      />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {headers.map((header, index) => (
                        <th key={index} scope="col" />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {childRows.map((childColumn, childRowIndex) => (
                      <TableRow key={childRowIndex}>
                        {columns.map((column, cellIndex) => {
                          const childColumnIndex = cellIndex - 1;
                          return (
                            <TableCell
                              key={`${childRowIndex}-${cellIndex}`}
                              style={{
                                width:
                                  customizedWidths &&
                                  customizedWidths[cellIndex],
                              }}
                            >
                              {cellIndex !== 0 &&
                              typeof childColumn[childColumnIndex] ===
                                'object' ? (
                                <UI
                                  components={childColumn[childColumnIndex]}
                                  context={tableRowProps}
                                />
                              ) : (
                                <TextOverflowTooltip
                                  tooltip={childColumn[childColumnIndex]}
                                  text={childColumn[childColumnIndex]}
                                  placement="top"
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </ChildCollapsibleRow>
      )}
    </>
  );
};

TableCollapsibleRow.defaultProps = {
  editableColumnChildren: {},
  activeRow: undefined,
};

TableCollapsibleRow.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.any).isRequired,
  rowIndex: PropTypes.number.isRequired,
  editableColumnChildren: PropTypes.objectOf(PropTypes.any),
  activeRow: PropTypes.number,
  childRows: PropTypes.arrayOf(PropTypes.any).isRequired,
  iconColumnIndex: PropTypes.number,
  table: PropTypes.shape({
    headers: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        sort: PropTypes.string,
        direction: PropTypes.string,
      }).isRequired,
    ).isRequired,
    customizedWidths: PropTypes.arrayOf(PropTypes.any).isRequired,
  }).isRequired,
};

export default TableCollapsibleRow;
